import GridLayout from 'react-grid-layout';
import { useMemo, useState, useEffect, useRef } from 'react';
import DraggableCard from './DraggableCard';
import { GRID_COLUMNS } from '../config/dashboardConfig';
import './GridLayoutGroup.css';

const GridLayoutGroup = ({
  groupId,
  groupLabel,
  cards,
  dashboardData,
  isEditMode,
  onCardToggle,
  onLayoutChange,
}) => {
  // react-grid-layout 형식으로 변환
  const [layoutState, setLayoutState] = useState([]);
  const [containerWidth, setContainerWidth] = useState(1200);
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggingCardId, setDraggingCardId] = useState(null);

  // cards prop 변경 추적
  useEffect(() => {
  }, [cards, groupId, isEditMode]);

  // 컨테이너 너비 측정
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setContainerWidth(width);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // 비활성화 그룹이 아닌 경우: 활성화된 카드만 표시 (편집 모드 여부와 관계없이)
  // 비활성화 그룹인 경우: 편집 모드에서만 모든 카드 표시
  const displayCards = useMemo(() => {
    if (groupId === 'disabledCommon' || groupId === 'disabledDrRelated') {
      // 편집 모드가 아니면 비활성화 그룹도 숨김
      const disabledCards = isEditMode ? cards : [];
      return disabledCards;
    }
    // 활성 그룹은 항상 활성화된 카드만 표시
    const enabledCards = cards.filter((card) => card.enabled === true);
    
    return enabledCards;
  }, [groupId, cards, isEditMode]);

  // cards prop이 변경될 때마다 layoutState 업데이트
  // cards 배열을 JSON.stringify로 변환하여 변경 감지
  const cardsKey = useMemo(() => {
    const key = JSON.stringify(cards.map(c => ({ id: c.id, enabled: c.enabled, x: c.x, y: c.y, sizeW: c.sizeW, sizeH: c.sizeH })));
    return key;
  }, [cards, groupId]);

  // displayCards 변경을 즉시 반영하기 위해 displayCardsKey도 생성
  const displayCardsKey = useMemo(() => {
    const key = JSON.stringify(displayCards.map(c => ({ id: c.id, enabled: c.enabled, x: c.x, y: c.y, sizeW: c.sizeW, sizeH: c.sizeH })));
    return key;
  }, [displayCards, groupId]);

  useEffect(() => {
    // displayCards가 비어있으면 빈 레이아웃 설정
    if (displayCards.length === 0) {
      setLayoutState([]);
      return;
    }

    const newLayout = displayCards.map((card, index) => {
      // 편집 모드이고 활성화된 카드만 드래그 가능
      // 비활성화된 카드는 드래그 불가
      const isStatic = !isEditMode || !card.enabled;
      
      // 비활성화 그룹의 경우 x, y가 없을 수 있으므로 순차적으로 배치
      let cardX = card.x || 1;
      let cardY = card.y || 1;
      
      if ((groupId === 'disabledCommon' || groupId === 'disabledDrRelated') && (!card.x || !card.y)) {
        // 비활성화 그룹에서 x, y가 없으면 자동 배치 (4열 기준)
        // 기존 카드들의 위치를 고려하여 겹치지 않도록 배치
        const occupiedPositions = new Set();
        displayCards.forEach((c, idx) => {
          if (idx < index && c.x && c.y) {
            for (let dx = 0; dx < (c.sizeW || 1); dx++) {
              for (let dy = 0; dy < (c.sizeH || 1); dy++) {
                occupiedPositions.add(`${c.x + dx},${c.y + dy}`);
              }
            }
          }
        });
        
        // 빈 위치 찾기
        const cardSizeW = card.sizeW || 1;
        const cardSizeH = card.sizeH || 1;
        let positionFound = false;
        
        for (let row = 1; row <= 100; row++) {
          for (let col = 1; col <= GRID_COLUMNS; col++) {
            let canPlace = true;
            for (let dx = 0; dx < cardSizeW; dx++) {
              for (let dy = 0; dy < cardSizeH; dy++) {
                const checkCol = col + dx;
                const checkRow = row + dy;
                if (checkCol > GRID_COLUMNS || occupiedPositions.has(`${checkCol},${checkRow}`)) {
                  canPlace = false;
                  break;
                }
              }
              if (!canPlace) break;
            }
            if (canPlace) {
              cardX = col;
              cardY = row;
              positionFound = true;
              break;
            }
          }
          if (positionFound) break;
        }
        
        // 위치를 찾지 못한 경우 기본 순차 배치
        if (!positionFound) {
          const col = (index % GRID_COLUMNS) + 1;
          const row = Math.floor(index / GRID_COLUMNS) + 1;
          cardX = col;
          cardY = row;
        }
      }
      
      return {
        i: card.id,
        x: cardX - 1, // react-grid-layout은 0부터 시작
        y: cardY - 1,
        w: card.sizeW || 1,
        h: card.sizeH || 1,
        minW: 1,
        minH: 1,
        maxW: GRID_COLUMNS,
        static: isStatic, // 비활성화된 카드는 항상 static
      };
    });
    
    setLayoutState(newLayout);
  }, [cardsKey, displayCardsKey, displayCards, isEditMode, groupId]);

  // 레이아웃 변경 핸들러
  const handleLayoutChange = (newLayout) => {
    if (!isEditMode) return;
    
    setLayoutState(newLayout);
    
    // react-grid-layout 형식에서 카드 형식으로 변환
    // displayCards에서 찾기 (현재 표시되는 카드들)
    const updatedCards = newLayout.map((item) => {
      const card = displayCards.find((c) => c.id === item.i);
      if (!card) {
        console.warn('handleLayoutChange: Card not found in displayCards', item.i);
        return null;
      }

      return {
        ...card,
        x: item.x + 1, // 1부터 시작하도록 변환
        y: item.y + 1,
        sizeW: item.w,
        sizeH: item.h,
      };
    }).filter(Boolean);

    if (onLayoutChange && updatedCards.length > 0) {
      onLayoutChange(groupId, updatedCards);
    }
  };

  // 비활성화 그룹은 편집 모드에서만 표시
  // displayCards가 비어있으면 아무것도 표시하지 않음
  if ((groupId === 'disabledCommon' || groupId === 'disabledDrRelated') && (!isEditMode || displayCards.length === 0)) {
    return null;
  }

  // 드래그 이벤트 핸들러
  const handleDragStart = (layout, oldItem, newItem, placeholder, e, element) => {
    setIsDragging(true);
    setDraggingCardId(oldItem.i);
  };

  const handleDragStop = (layout, oldItem, newItem, placeholder, e, element) => {
    setIsDragging(false);
    setDraggingCardId(null);
  };

  // 드래그 중인 카드 정보
  const draggingCard = draggingCardId ? displayCards.find(c => c.id === draggingCardId) : null;

  return (
    <div className="grid-layout-group">
      {/* 편집 모드일 때 그룹 타이틀 표시 */}
      {isEditMode && groupLabel && (
        <div className="group-label text-h2 text-gray-800 mb-16">
          {groupLabel}
        </div>
      )}
      <div
        ref={containerRef}
        className={`grid-layout-container ${isEditMode ? 'edit-mode' : ''} ${groupId === 'disabled' ? 'disabled-group' : ''} ${isDragging && draggingCardId ? 'dragging-active' : ''}`}
      >
        <GridLayout
          className="dashboard-grid-layout"
          layout={layoutState}
          cols={GRID_COLUMNS}
          rowHeight={180}
          width={containerWidth}
          isDraggable={isEditMode}
          isResizable={false} // 크기 조절 비활성화 (사전 정의된 크기만 사용)
          onLayoutChange={handleLayoutChange}
          onDragStart={handleDragStart}
          onDragStop={handleDragStop}
          margin={[24, 24]}
          containerPadding={[0, 0]}
          preventCollision={true}
          compactType={null} // 자동 압축 비활성화 (Y 좌표 유지)
          useCSSTransforms={true}
          draggableHandle=".draggable-card.edit-mode:not(.disabled)" // 드래그 핸들 지정
          cancel=".toggle-card-btn, .card-edit-overlay, button" // 버튼 클릭 시 드래그 취소
        >
          {layoutState.length === 0 ? (
            <div className="empty-layout-message">
              {groupId === 'disabled' ? '비활성화된 카드가 없습니다' : '표시할 카드가 없습니다'}
            </div>
          ) : (
            layoutState.map((item) => {
              // displayCards에서 직접 찾기 (더 정확함)
              const card = displayCards.find((c) => c.id === item.i);
              if (!card) {
                console.warn('Card not found in displayCards:', item.i, {
                  groupId,
                  displayCardsIds: displayCards.map(c => c.id),
                  layoutItemIds: layoutState.map(l => l.i)
                });
                return null;
              }

              return (
                <div key={card.id} className="grid-item-wrapper">
                  <DraggableCard
                    card={card}
                    dashboardData={dashboardData}
                    isEditMode={isEditMode}
                    onToggle={onCardToggle}
                    group={groupId}
                  />
                </div>
              );
            })
          )}
        </GridLayout>
      </div>
    </div>
  );
};

export default GridLayoutGroup;

